import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Reusable section component
const UseCaseSection = ({ title, children, bgColor = 'bg-white' }) => (
  <section className={`py-16 ${bgColor}`}>
    <div className="container mx-auto">
      <h2 className="text-3xl font-bold mb-8">{title}</h2>
      <div className="prose prose-lg max-w-none">
        {children}
      </div>
    </div>
  </section>
);

// Icon for bullet points
const BulletIcon = () => (
  <svg className="inline-block w-5 h-5 mr-2 text-primary" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

const UseCasePageTemplate = ({ 
  title, 
  description,
  heroAnimation,
  heroSvg,
  icon,
  challenges = [],
  solutions = [],
  benefits = [],
  testimonial = null,
  relatedUsesCases = [],
  additionalContent = null
}) => {
  return (
    <main className="pt-24 pb-16">
      {/* Hero section */}
      <section className="bg-gradient-to-b from-purple-50 to-white py-20">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="md:w-1/2"
            >
              <div className="bg-purple-100 p-4 rounded-lg w-20 h-20 flex items-center justify-center text-primary mb-6">
                {icon}
              </div>
              <h1 className="text-4xl font-bold mb-6">{title}</h1>
              <p className="text-xl text-gray-700 mb-8">{description}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="/#request-demo" className="button button-primary">
                  Request a Demo
                </a>
                <a href="/contact" className="button button-secondary">
                  Contact Sales
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="md:w-1/2"
            >
              {/* Display either SVG or Image */}
              <div className="hero-section">
                {heroAnimation || heroSvg}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenges section */}
      {challenges.length > 0 && (
        <UseCaseSection title="Key Challenges" bgColor="bg-white">
          <div className="space-y-6">
            {challenges.map((challenge, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                <h3 className="text-xl font-bold mb-2">{challenge.title}</h3>
                <p>{challenge.description}</p>
              </div>
            ))}
          </div>
        </UseCaseSection>
      )}

      {/* Solutions section */}
      {solutions.length > 0 && (
        <UseCaseSection title="How Qupid Helps" bgColor="bg-gray-50">
          <div className="space-y-8">
            {solutions.map((solution, index) => (
              <div key={index} className="flex flex-col md:flex-row gap-8 bg-white p-6 rounded-lg shadow-sm">
                <div className="md:w-1/4">
                  <div className="bg-purple-100 p-4 rounded-lg w-16 h-16 flex items-center justify-center text-primary">
                    {solution.icon}
                  </div>
                </div>
                <div className="md:w-3/4">
                  <h3 className="text-xl font-bold mb-3">{solution.title}</h3>
                  <p className="text-gray-700 mb-4">{solution.description}</p>
                  {solution.items && (
                    <ul className="space-y-2">
                      {solution.items.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <BulletIcon />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </UseCaseSection>
      )}

      {/* Benefits section */}
      {benefits.length > 0 && (
        <UseCaseSection title="Business Impact" bgColor="bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="bg-primary bg-opacity-10 p-3 rounded-full text-primary mr-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg font-bold">{benefit.title}</h3>
                </div>
                <p className="text-gray-700">{benefit.description}</p>
              </div>
            ))}
          </div>
        </UseCaseSection>
      )}

      {/* Testimonial section - temporarily disabled 
      {testimonial && (
        <UseCaseSection title="" bgColor="bg-gray-50">
          <div className="bg-white rounded-xl shadow-md p-8 border border-gray-100">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {testimonial.image && (
                <div className="md:w-1/4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name} 
                    className="w-32 h-32 rounded-full mx-auto"
                  />
                </div>
              )}
              <div className="md:w-3/4">
                <p className="text-xl italic mb-6">"{testimonial.quote}"</p>
                <div>
                  <p className="font-bold">{testimonial.author}</p>
                  <p className="text-gray-600">{testimonial.title}, {testimonial.company}</p>
                </div>
              </div>
            </div>
          </div>
        </UseCaseSection>
      )}
      */}

      {/* Additional content */}
      {additionalContent && (
        <UseCaseSection title="Learn More" bgColor="bg-white">
          {additionalContent}
        </UseCaseSection>
      )}

      {/* Related Use Cases */}
      {relatedUsesCases.length > 0 && (
        <UseCaseSection title="Related Use Cases" bgColor="bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedUsesCases.map((useCase, index) => (
              <Link 
                key={index} 
                to={useCase.href} 
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center mb-4">
                  <div className="bg-primary bg-opacity-10 p-3 rounded-full text-primary mr-4">
                    {useCase.icon}
                  </div>
                  <h3 className="text-lg font-bold">{useCase.title}</h3>
                </div>
                <p className="text-gray-700">{useCase.description}</p>
              </Link>
            ))}
          </div>
        </UseCaseSection>
      )}

      {/* CTA section */}
      <section className="bg-purple-700 text-white py-16">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Data Operations?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            See how Qupid can help your team address these challenges and drive better business outcomes with trusted data.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/#request-demo" className="button bg-white text-purple-700 hover:bg-gray-100">
              Request a Demo
            </a>
            <a href="/contact" className="button bg-transparent border-2 border-white hover:bg-white hover:text-purple-700">
              Contact Sales
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default UseCasePageTemplate; 